import {
  isSameStreet,
  isSameHouseNumberAndMailbox,
} from '@kathondvla/sri-client/address-utils';
import {
  getNow,
  getPreviousDay,
  isAfter,
  isAfterOrEqual,
  isBefore,
  isBeforeOrEqual,
} from '@kathondvla/sri-client/date-utils';
import { generateUUID } from '@kathondvla/sri-client/common-utils';
import Batch from '@kathondvla/sri-client/batch';
import { priority } from 'ReduxLoop/utils/contactDetailsResourceFactory';
import { OU_TYPE_CONFIG } from './typeConfig';
import { copy } from 'angular';
import { cpuUsage } from 'process';

export const getKeyAndMeta = (path) => {
  const key = generateUUID();
  return {
    $$meta: { permalink: `${path}/${key}` },
    key,
  };
};

export const createOuResource = (ouType, name, shortName, startDate, ouTypeConfig) => ({
  ...getKeyAndMeta(`/${ouTypeConfig.domain}/organisationalunits`),
  type: ouType,
  startDate,
  names: [
    {
      type: 'OFFICIAL',
      startDate,
      value: name,
    },
    {
      type: 'SHORT',
      startDate,
      value: shortName,
    },
  ],
});

const findNameObjOnDate = (names, type, date) => {
  const ret = names.find(
    (nameObj) =>
      nameObj.type === type &&
      isBeforeOrEqual(nameObj.startDate, date) &&
      ((!date && !nameObj.endDate) || isAfter(nameObj.endDate, date))
  );
  return { ...ret };
};

const getOuNamesOfType = (name, type, ou, newEndDate, dateOfNameChange) => {
  // console.log('ou.names', ou.names);
  const currentNameObj = findNameObjOnDate(ou.names, type, ou.endDate && !newEndDate ? getPreviousDay(ou.endDate) : dateOfNameChange);
  if (!currentNameObj.value) {
    console.error(
      `not a current ${type.toLowerCase()} name found on ${getPreviousDay(ou.endDate)}!`,
      ou.names
    );
  }
  const previousNameObj = findNameObjOnDate(ou.names, type, getPreviousDay(currentNameObj.startDate));
  // console.log('current name obj', currentNameObj);
  if (isBeforeOrEqual(newEndDate, currentNameObj.startDate)) {
    const lastValidNameObj = findNameObjOnDate(ou.names, type, newEndDate);
    lastValidNameObj.endDate = newEndDate;
    // lastValidNameObj.value = name;
    return [
      ...ou.names.filter(
        (nameObj) =>
          nameObj.type === type && isBefore(nameObj.startDate, lastValidNameObj.startDate)
      ),
      lastValidNameObj,
    ];
  }
  const newNames = ou.names.filter(
    (nameObj) => nameObj.type === type && isBefore(nameObj.startDate, currentNameObj.startDate)
  );
  if (previousNameObj && name === previousNameObj.value) {
    newNames[newNames.length - 1].endDate = newEndDate;
    return newNames;
  }
  newNames.push(currentNameObj);
  if (newEndDate !== currentNameObj.endDate) {
    console.warn(
      'endDate on the ou and not on the current name object. We will also end the current name'
    );
    currentNameObj.endDate = newEndDate;
  }
  if (currentNameObj.value === name) {
    return newNames;
  }
  if (currentNameObj.startDate === dateOfNameChange) {
    currentNameObj.value = name;
    return newNames;
  }
  currentNameObj.endDate = dateOfNameChange;
  newNames.push({
    type,
    startDate: dateOfNameChange,
    endDate: newEndDate,
    value: name,
  });
  return newNames;
};

export const createExternalIdentifier = (ouHref, value, type, ouTypeConfig) => ({
  ...getKeyAndMeta(`/${ouTypeConfig.domain}/organisationalunits/externalidentifiers`),
  type,
  organisationalUnit: { href: ouHref },
  value,
});

export const createLegalOrganisationalForm = (ouHref, legalOrganisationalFormHref, startDate, endDate, ouTypeConfig, legalFormList) => ({
  ...getKeyAndMeta(`/${ouTypeConfig.domain}/organisationalunits/legalorganisationalforms`),
  organisationalUnit: { href: ouHref },
  legalOrganisationalForm: { href: legalOrganisationalFormHref, $$expanded: legalFormList.find((elem) => elem.$$meta.permalink === legalOrganisationalFormHref) },
  startDate,
  endDate
});

const createContactDetail = (ouHref, type, value, ouTypeConfig) => ({
  ...getKeyAndMeta(`/${ouTypeConfig.domain}/organisationalunits/contactdetails`),
  type,
  organisationalUnit: { href: ouHref },
  value,
  priority: priority(type),
});

const createSeat = (ouHref, physicalLocation, startDate, endDate, ouTypeConfig) => ({
  ...getKeyAndMeta(`/${ouTypeConfig.domain}/organisationalunits/locations`),
  type: 'SEAT',
  organisationalUnit: { href: ouHref },
  physicalLocation: { href: physicalLocation.$$meta.permalink, $$expanded: physicalLocation },
  startDate,
  endDate,
});

export const calculateChanges = ({
  ouType,
  ouModel,
  formModel,
  dirtyResources,
  pendingResources,
  ouData,
  physicalLocationsInCityOfMail,
  legalFormList,
}) => {
  const ouTypeConfig = OU_TYPE_CONFIG[ouType];
  const newOuData = { ...ouData };
  const batch = new Batch();
  const newOu = ouModel
    ? { ...ouModel.resource }
    : createOuResource(ouType, formModel.name, formModel.shortName, formModel.startDate, ouTypeConfig);
  if (dirtyResources.ou) {
    // if shortName is not stored historical, it just always overwrites the SHORTNAME for the entire period of the OU
    newOu.names = ouTypeConfig.components.shortName.historical ? 
      [
        ...getOuNamesOfType(formModel.name, 'OFFICIAL', newOu, formModel.endDate, formModel.dateOfNameChange),
        ...getOuNamesOfType(formModel.shortName, 'SHORT', newOu, formModel.endDate, formModel.dateOfNameChange),
      ]
      : [
        ...getOuNamesOfType(formModel.name, 'OFFICIAL', newOu, formModel.endDate, formModel.dateOfNameChange),
        {
          ...findNameObjOnDate(newOu.names, 'SHORT', getPreviousDay(newOu.endDate)),
          value: formModel.shortName,
          endDate: formModel.endDate,
        },
      ];
    if (formModel.callName) {
      newOu.names.push({
        type: 'CALL',
        startDate: formModel.startDate,
        endDate: formModel.endDate,
        value: formModel.callName,
      });
    }
    newOu.description = formModel.description;
    newOu.startDate = formModel.startDate;
    if (ouModel && newOu.startDate !== ouModel.resource.startDate) {
      newOu.names = newOu.names.map((nameObj) => {
        if (nameObj.startDate === ouModel.resource.startDate) {
          return { ...nameObj, startDate: formModel.startDate };
        }
        return nameObj;
      });
    }
    newOu.endDate = formModel.endDate;
    newOuData.ouResource = newOu;
    batch.put(newOu);
  }
  if (!ouModel && ouTypeConfig.components.institutionNumber && formModel.institutionNumber) {
    batch.put(
      createExternalIdentifier(
        newOu.$$meta.permalink,
        formModel.institutionNumber,
        'INSTITUTION_NUMBER',
        ouTypeConfig
      )
    );
  }

  if (!newOuData.identifiers) {
    newOuData.identifiers = [];
  }
  if (dirtyResources.companyNumber) {
    if (formModel.companyNumber && (!ouModel || !ouModel.companyNumber)) {
      const companyNumber = createExternalIdentifier(
        newOu.$$meta.permalink,
        formModel.companyNumber,
        'COMPANY_NUMBER',
        ouTypeConfig
      );
      newOuData.identifiers = [companyNumber, ...newOuData.identifiers];
      batch.put(companyNumber);
    } else if (!formModel.companyNumber && ouModel && ouModel.companyNumber) {
      newOuData.identifiers = ouData.identifiers.filter(
        (id) => id.$$meta.permalink !== ouModel.companyNumber.$$meta.permalink
      );
      batch.delete(ouModel.companyNumber.$$meta.permalink);
    } else if (formModel.companyNumber && ouModel && formModel.companyNumber !== ouModel.companyNumber.value) {
      const companyNumber = { ...ouModel.companyNumber, value: formModel.companyNumber };
      newOuData.identifiers = [
        companyNumber,
       ...ouData.identifiers.filter(
          (id) => id.$$meta.permalink !== ouModel.companyNumber.$$meta.permalink
        ),
      ];
      batch.put(companyNumber);
    }
  }
  if (dirtyResources.vatNumber) {
    if (formModel.vatNumber && (!ouModel || !ouModel.vatNumber)) {
      const vatNumber = createExternalIdentifier(
        newOu.$$meta.permalink,
        formModel.vatNumber,
        'VAT_NUMBER',
        ouTypeConfig
      );
      newOuData.identifiers = [vatNumber, ...newOuData.identifiers];
      batch.put(vatNumber);
    } else if (!formModel.vatNumber && ouModel && ouModel.vatNumber) {
      newOuData.identifiers = ouData.identifiers.filter(
        (id) => id.$$meta.permalink !== ouModel.vatNumber.$$meta.permalink
      );
      batch.delete(ouModel.vatNumber.$$meta.permalink);
    } else if (formModel.vatNumber && ouModel && formModel.vatNumber !== ouModel.vatNumber.value) {
      const vatNumber = { ...ouModel.vatNumber, value: formModel.vatNumber };
      newOuData.identifiers = [
        vatNumber,
        ...ouData.identifiers.filter(
          (id) => id.$$meta.permalink !== ouModel.vatNumber.$$meta.permalink
        ),
      ];
      batch.put(vatNumber);
    }
  }

  if (dirtyResources.legalForm) {
    if (formModel.legalForm && (!ouModel || !ouModel.legalOrganisationalForm)) {
      const legOrgForm = createLegalOrganisationalForm(
        newOu.$$meta.permalink,
        formModel.legalForm.$$meta.permalink,
        newOu.startDate,
        newOu.endDate,
        ouTypeConfig,
        legalFormList
      );
      newOuData.legalOrganisationalForm = legOrgForm;
      batch.put(legOrgForm);
    } else if ((!formModel.legalForm || !formModel.legalForm.key) && ouModel && ouModel.legalOrganisationalForm) {
      newOuData.legalOrganisationalForm = null;
      batch.delete(ouModel.legalOrganisationalForm.$$meta.permalink);
    } else if (formModel.legalForm && ouModel && formModel.legalForm.$$meta.permalink !== ouModel.legalOrganisationalForm.legalOrganisationalForm.href) {
      const legOrgForm = { ...ouData.legalOrganisationalForm, legalOrganisationalForm: { href: formModel.legalForm.$$meta.permalink, $$expanded: formModel.legalForm } };
      newOuData.legalOrganisationalForm = legOrgForm;
      batch.put(legOrgForm);
    }
  }

  newOuData.contactDetails = [];
  if (dirtyResources.mail || dirtyResources.dateOfAddressChange) {
    // also make sure the seat address is a historical representation of the contact mail
    let seatReferenceDate = formModel.dateOfAddressChange || getNow();
    let currentSeat = ouModel ? ouData.locations.find(
      (loc) =>
        loc.type === 'SEAT' &&
        isBeforeOrEqual(loc.startDate, seatReferenceDate) &&
        ((formModel.endDate && loc.endDate === formModel.endDate) || isAfter(loc.endDate, seatReferenceDate))
    ) : null;
    console.log('current seat', currentSeat);
    const existingPhysicalLocation = !formModel.mail
      ? null
      : physicalLocationsInCityOfMail.find(
          (pl) =>
            isSameStreet(pl.address, formModel.mail) &&
            isSameHouseNumberAndMailbox(pl.address, formModel.mail)
        );
    const physicalLocation = existingPhysicalLocation || {
      ...getKeyAndMeta('/sam/physicallocations'),
      type: 'DOMAIN',
      address: formModel.mail,
      status: 'IN_USE',
    };
    const seatThatEndsOnReferenceDate = ouModel ? ouData.locations.find(
      (loc) => loc.type === 'SEAT' && loc.endDate === seatReferenceDate
    ) : null;
    if (seatThatEndsOnReferenceDate) {
      console.log('seat that ends on reference date', seatThatEndsOnReferenceDate);
      if (seatThatEndsOnReferenceDate.physicalLocation.href === physicalLocation.$$meta.permalink) {
        console.log('seat that ended on reference date is the same as the given address');
        // seatsThatNeedToBeDeleted.push(currentSeat);
        currentSeat = seatThatEndsOnReferenceDate;
        seatReferenceDate = seatThatEndsOnReferenceDate.startDate;
      }
    }

    const seatsThatNeedToBeDeleted = ouModel ? newOuData.locations.filter(
      (seat) => isAfterOrEqual(seat.startDate, currentSeat.endDate)
    ) : [];

    if (formModel.mail && formModel.mail.city && (!ouModel || !ouModel.mail)) {
      const cd = createContactDetail(newOu.$$meta.permalink, 'MAIL', { ...formModel.mail, streetHref: undefined }, ouTypeConfig);
      batch.put(cd);
      newOuData.contactDetails.push(cd);
      if (!ouTypeConfig.components.mail || !ouTypeConfig.components.mail.noSeat) {
        const newSeat = createSeat(
          newOu.$$meta.permalink,
          physicalLocation,
          !newOuData.locations.some((loc) => loc.type === 'SEAT') ? newOu.startDate : seatReferenceDate,
          formModel.endDate,
          ouTypeConfig
        );
        batch.put(newSeat);
        if (!existingPhysicalLocation) {
          batch.put(physicalLocation);
        }
        newOuData.locations.push(newSeat);
      }
    } else if (!(formModel.mail && formModel.mail.city) && ouModel && ouModel.mail) {
      batch.delete(ouModel.mail.$$meta.permalink);
      if (!ouTypeConfig.components.mail.noSeat) {
        throw new Error('not.allowed.to.delete.seat');
        if (isAfterOrEqual(currentSeat.startDate, seatReferenceDate)) {
          newOuData.locations = newOuData.locations.filter((loc) => loc.key !== currentSeat.key);
          batch.delete(currentSeat.$$meta.permalink);
        }
        if (
          currentSeat.endDate !== seatReferenceDate &&
          !(currentSeat.endDate === formModel.endDate && isBeforeOrEqual(formModel.endDate, seatReferenceDate))
        ) {
          const newSeat = { ...currentSeat, endDate: seatReferenceDate };
          newOuData.locations = [
            ...newOuData.locations.filter((loc) => loc.key !== currentSeat.key),
            newSeat,
          ];
          batch.put(newSeat);
        }
      }
    } else if (formModel.mail && formModel.mail.city && ouModel) {
      const cd = { ...ouModel.mail, value: { ...formModel.mail } };
      newOuData.contactDetails.push(cd);
      batch.put(cd);
      if (!ouTypeConfig.components.mail.noSeat && isAfter(formModel.endDate, seatReferenceDate)) {
        if (currentSeat.physicalLocation.href === physicalLocation.$$meta.permalink) {
          // if the physical location of the seat is not changed, the startDate needs to change to the date of address change chosen by the user
          const previousSeat = ouData.locations.find(
            (loc) => loc.type === 'SEAT' && loc.endDate === currentSeat.startDate
          );
          const newSeat = {
            ...currentSeat,
            startDate: seatReferenceDate,
            endDate: formModel.endDate,
          };
          newOuData.locations = newOuData.locations.filter((loc) => loc.key !== currentSeat.key && (!previousSeat || loc.key !== previousSeat.key));
          newOuData.locations.push(newSeat);
          batch.put(newSeat);

          if (previousSeat) {
            const newPreviousSeat = {
              ...previousSeat,
              endDate: seatReferenceDate
            }
            newOuData.locations.push(newPreviousSeat);
            batch.put(newPreviousSeat);
          }
        } else if (ouModel && isAfterOrEqual(currentSeat.startDate, getPreviousDay(seatReferenceDate, 21))) {
          // we do not register a historical seat change of only 3 weeks. If the previous seat is only 3 weeks old, we just overwrite the physical location of the old seat
          const newSeat = {
            ...currentSeat,
            physicalLocation: {
              href: physicalLocation.$$meta.permalink,
              $$expanded: physicalLocation,
            },
            endDate: formModel.endDate,
          }; // startDate is unchanged
          batch.put(newSeat);
          newOuData.locations = [
            ...newOuData.locations.filter((loc) => loc.key !== currentSeat.key),
            newSeat,
          ];
        } else {
          // a real address change after more than 3 weeks: end the current seat and create a new one with new physical location
          const previousSeat = { ...currentSeat, endDate: seatReferenceDate };
          const deletedSeatOnSamePhysicalLocationIndex = seatsThatNeedToBeDeleted.findIndex(
            (seat) => seat.physicalLocation.href === physicalLocation.$$meta.permalink
          );
          const newSeat = deletedSeatOnSamePhysicalLocationIndex !== -1
            ? { ...seatsThatNeedToBeDeleted[deletedSeatOnSamePhysicalLocationIndex], startDate: seatReferenceDate, endDate: formModel.endDate }
            : createSeat(
            newOu.$$meta.permalink,
            physicalLocation,
            seatReferenceDate,
            formModel.endDate,
            ouTypeConfig
          );
          if (deletedSeatOnSamePhysicalLocationIndex !== -1) {
            seatsThatNeedToBeDeleted.splice(deletedSeatOnSamePhysicalLocationIndex, 1)
          }
          batch.put(previousSeat);
          batch.put(newSeat);
          newOuData.locations = [
            ...newOuData.locations.filter((loc) => loc.key !== currentSeat.key && newSeat.key !== loc.key),
            previousSeat,
            newSeat,
          ];
        }
        if (!existingPhysicalLocation) {
          batch.put(physicalLocation);
        }
      }
    }
    if (seatsThatNeedToBeDeleted.length) {
      console.warn('seats that need to be deleted', seatsThatNeedToBeDeleted);
      seatsThatNeedToBeDeleted.forEach((seat) => {
        batch.delete(seat.$$meta.permalink);
        newOuData.locations = [
          ...newOuData.locations.filter((loc) => loc.key !== seat.key),
        ];
      });
    }
    if (
      !ouTypeConfig.components.mail.noSeat &&
      newOuData.locations.filter((loc => loc.type === 'SEAT')).length === 1 &&
      newOuData.locations[0].startDate !== newOuData.ouResource.startDate
    ) {
      throw new Error('first.seat.startDate.should.be.ou.startDate');
    }
  } else if (ouModel && ouModel.mail) {
    newOuData.contactDetails.push(ouModel.mail);
  }

  if (!ouModel) {
    return { batch: batch.array, newOuData };
  }

  const pendingRelations = pendingResources.relations;

  if (formModel.endDate !== ouModel.endDate) {
    console.warn('endDate changed');

    if (newOuData.legalOrganisationalForm) {
      batch.put({ ...newOuData.legalOrganisationalForm, endDate: formModel.endDate });
    }

    // adapt seat addresses (not managed in screen)
    newOuData.locations = newOuData.locations.filter((loc) => loc.type !== 'SEAT');
    ouData.locations
      .filter(
        (seat) =>
          seat.type === 'SEAT' &&
          (isAfter(seat.endDate, formModel.endDate) ||
            (!formModel.endDate && seat.endDate === ouModel.endDate))
      )
      .forEach((seat) => {
        if (isBeforeOrEqual(formModel.endDate, seat.startDate)) {
          batch.delete(seat.$$meta.permalink);
        } else {
          newOuData.locations.push(seat);
          batch.put({ ...seat, endDate: formModel.endDate });
        }
      });
    // edge case to also adapt relation to an endDate that makes relations obsolete
    ouData.relations
      .filter(
        (existingRel) =>
          !Object.keys(pendingRelations).some((href) => existingRel.$$meta.permalink === href) &&
          isBefore(formModel.endDate, existingRel.endDate)
      )
      .forEach((relAffectedByNewEndDate) => {
        console.warn('historical rel updated because of new end date', relAffectedByNewEndDate);
        pendingRelations[relAffectedByNewEndDate.$$meta.permalink] = {
          ...relAffectedByNewEndDate,
          endDate: formModel.endDate,
        };
      });
  }

  newOuData.relations = ouData.relations.filter(
    (existingRel) =>
      !Object.keys(pendingRelations).some((href) => existingRel.$$meta.permalink === href)
  );
  Object.values(pendingRelations).forEach((rel) => {
    if (isBeforeOrEqual(formModel.endDate, rel.startDate) || isBeforeOrEqual(rel.endDate, rel.startDate)) {
      batch.delete(rel.$$meta.permalink);
    } else {
      newOuData.relations.push(rel);
      batch.put(rel);
    }
  });

  newOuData.locations = ouData.locations.filter(
    (existingRel) =>
      !Object.keys(pendingResources.locations).some((href) => existingRel.$$meta.permalink === href)
  );
  Object.values(pendingResources.locations).forEach((loc) => {
    if (isBeforeOrEqual(formModel.endDate, loc.startDate)) {
      batch.delete(loc.$$meta.permalink);
    } else {
      newOuData.locations.push(loc);
      batch.put(loc);
    }
  });
  Object.values(pendingResources.physicalLocations).forEach((pl) => {
    batch.put(pl);
  });

  if (dirtyResources.email) {
    if (formModel.email && !ouModel.email) {
      const cd = createContactDetail(newOu.$$meta.permalink, 'EMAIL', { email: formModel.email }, ouTypeConfig);
      newOuData.contactDetails.push(cd);
      batch.put(cd);
    } else if (!formModel.email && ouModel.email) {
      batch.delete(ouModel.email.$$meta.permalink);
    } else if (formModel.email) {
      const cd = { ...ouModel.email, value: { email: formModel.email } };
      newOuData.contactDetails.push(cd);
      batch.put(cd);
    }
  } else if (ouModel.email) {
    newOuData.contactDetails.push(ouModel.email);
  }
  if (dirtyResources.phone) {
    if (formModel.phone && !ouModel.phone) {
      const cd = createContactDetail(newOu.$$meta.permalink, 'PHONE', { phone: formModel.phone }, ouTypeConfig);
      newOuData.contactDetails.push(cd);
      batch.put(cd);
    } else if (!formModel.phone && ouModel.phone) {
      batch.delete(ouModel.phone.$$meta.permalink);
    } else if (formModel.phone) {
      const cd = { ...ouModel.phone, value: { phone: formModel.phone } };
      newOuData.contactDetails.push(cd);
      batch.put(cd);
    }
  } else if (ouModel.phone) {
    newOuData.contactDetails.push(ouModel.phone);
  }
  if (dirtyResources.website) {
    if (formModel.website && !ouModel.website) {
      const cd = createContactDetail(newOu.$$meta.permalink, 'WEBSITE', {
        website: formModel.website,
      }, ouTypeConfig);
      newOuData.contactDetails.push(cd);
      batch.put(cd);
    } else if (!formModel.website && ouModel.website) {
      batch.delete(ouModel.website.$$meta.permalink);
    } else if (formModel.website) {
      const cd = { ...ouModel.website, value: { website: formModel.website } };
      newOuData.contactDetails.push(cd);
      batch.put(cd);
    }
  } else if (ouModel.website) {
    newOuData.contactDetails.push(ouModel.website);
  }

  // console.log('batch is', batch.array);
  // throw new Error('We do not save to debug');

  return { batch: batch.array, newOuData };
};

export const deleteOu = (ouData, ouTypeConfig) => {
  const batch = new Batch();
  batch.delete(ouData.ouResource.$$meta.permalink);
  (ouData.contactDetails || []).forEach(cd => {
    batch.delete(cd.$$meta.permalink);
  });

  (ouData.relations || []).forEach(rel => {
    batch.delete(rel.$$meta.permalink);
  });

  (ouData.locations || []).forEach(loc => {
    batch.delete(loc.$$meta.permalink);
  });

  (ouData.identifiers || []).forEach(id => {
    batch.delete(id.$$meta.permalink);
  });

  if (ouData.legalOrganisationalForm && ouData.legalOrganisationalForm.$$meta && ouData.legalOrganisationalForm.$$meta.permalink) {
    batch.delete(ouData.legalOrganisationalForm.$$meta.permalink);
  }
  return { batch: batch.array };
}